import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  BestPriceGuaranteeLink,
  LocationAutocompleteTrigger,
  NumberPickerDialog,
} from "@apac/components/ui";
import { useBooleanFlag } from "@apac/feature-flags";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FeatureFlagNames,
  HOTELS_SEARCH_CALENDAR_PATH,
  HOTELS_SEARCH_LOCATION_PATH,
} from "@hsbc/utilities";
import { useHotelSearch, type HotelSearchComponentProps } from "@tenant/hotels";
import { Button, ButtonTypeEnum, DatePickerButton, Icon } from "@tenant/ui";
import clsx from "clsx";
import { GuestPicker } from "..";
import styles from "./styles.module.scss";

const MIN_ROOMS = 1;
const MAX_ROOMS = 4;

export const HotelSearch = ({
  children,
  className,
  onSubmit,
}: HotelSearchComponentProps & {
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
}) => {
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.HotelSearch);

  const { context, handlers } = useHotelSearch();
  const navigate = useNavigate();

  const enableBPG = useBooleanFlag(FeatureFlagNames.BEST_PRICE_GUARANTEE);
  const enableMultiRoom = useBooleanFlag(
    FeatureFlagNames.ENABLE_HOTELS_MULTIROOM
  );

  const roomsCount = context.roomsCount ?? 1;
  const adultsCount = context.guests?.adults ?? 0;
  const isRoomsCountValid =
    roomsCount >= MIN_ROOMS &&
    roomsCount <= MAX_ROOMS &&
    adultsCount >= roomsCount;
  const isFormValid = context.isFormValid && isRoomsCountValid;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (!isFormValid) {
      event.preventDefault();
      return;
    }
    handlers.onFormSubmit(event);
    onSubmit?.(event);
  };

  return (
    <div className={clsx(block, className)}>
      <form
        className={cn("form", { "multi-room": enableMultiRoom })}
        onSubmit={handleSubmit}
      >
        <div className={clsx(cn("field"), cn("location"))}>
          <span className={cn("label")}>{t("searchControl.whereTo")}</span>
          <Button
            buttonType={ButtonTypeEnum.Wrap}
            onClick={() => navigate(HOTELS_SEARCH_LOCATION_PATH)}
            aria-label={t("searchControl.whereTo")}
          >
            <LocationAutocompleteTrigger
              label={t("home.locationSearchLabel")}
              value={context.place}
            />
          </Button>
        </div>

        <div className={clsx(cn("field"), cn("dates"))}>
          <span className={cn("label")}>{t("chooseDates")}</span>
          <Button
            buttonType={ButtonTypeEnum.Wrap}
            onClick={() => navigate(HOTELS_SEARCH_CALENDAR_PATH)}
          >
            <DatePickerButton
              startDate={context.dateRange?.from}
              endDate={context.dateRange?.until}
              startLabel={t("core-hotels.search.date.startDate")}
              endLabel={t("core-hotels.search.date.endDate")}
              hideSeparator={true}
              ariaLabel={t("chooseDates")}
            />
          </Button>
        </div>

        <div className={clsx(cn("field"), cn("passengers"))}>
          <span className={cn("label")}>
            {t("passengerInformation.guests")}
          </span>
          <GuestPicker
            onGuestSelectionHandler={handlers.handleGuestsChange}
            guests={context.guests}
          />
        </div>

        {enableMultiRoom && (
          <div className={clsx(cn("field"), cn("numRooms"))}>
            <span className={cn("label")}>{t("rooms")}</span>
            <NumberPickerDialog
              label={t("rooms")}
              defaultValue={roomsCount}
              minimumValue={MIN_ROOMS}
              maximumValue={MAX_ROOMS}
              updateCount={handlers.handleRoomsCountChange}
            >
              <button
                type="button"
                className={cn("numRooms-button")}
                aria-label={t("recentSearch.hotels.rooms", {
                  count: roomsCount,
                })}
              >
                {t("recentSearch.hotels.rooms", { count: roomsCount })}
                <Icon
                  iconName={IconNameEnum.bedFilled}
                  className={cn("numRooms-button-icon")}
                />
              </button>
            </NumberPickerDialog>
          </div>
        )}

        <button
          className={cn("search-button")}
          type="submit"
          disabled={!isFormValid}
          aria-label={context.searchButtonLabel}
        >
          {context.searchButtonLabel}
        </button>
        {children}
        {enableBPG === true && (
          <BestPriceGuaranteeLink iconName={IconNameEnum.cashBag} />
        )}
      </form>
    </div>
  );
};
